import React, { useEffect, useCallback, useMemo } from 'react';
import {
  Box, Stack, Typography, IconButton, Chip,
  InputBase, useTheme, Paper, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Checkbox, Button, Menu, MenuItem, LinearProgress, Card, CardContent,
  Grid, Tooltip, Badge, TablePagination, Divider
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import SortIcon from '@mui/icons-material/Sort';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import EditIcon from '@mui/icons-material/Edit';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import HomeIcon from '@mui/icons-material/Home';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import styled from '@emotion/styled';
import { alpha } from '@mui/material/styles';
import { useAuth } from '../util/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import { collection, query, where, getDocs, doc, writeBatch } from 'firebase/firestore';
import { db } from '../config/firebase';
import { keyframes } from '@emotion/react';
import { motion, AnimatePresence, useMotionValue, useTransform } from 'framer-motion';
import { useS } from 'use-s-react';
import { store } from '../store';

// Animation variants for stagger effects
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.01,
      delayChildren: 0,
    }
  }
};

const rowVariants = {
  hidden: {
    opacity: 0,
    y: 8,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.15,
      ease: 'easeOut'
    }
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.1
    }
  }
};

const cardVariants = {
  hidden: {
    opacity: 0,
    y: 12,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.2,
      ease: 'easeOut'
    }
  }
};

const statsVariants = {
  hidden: { opacity: 0, y: -8 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.2,
      ease: 'easeOut'
    }
  }
};

// Function to get theme-aware colors
const getColors = (theme) => {
  const isDark = theme.palette.mode === 'dark';
  return {
    primary: theme.palette.primary.main,
    primaryDark: theme.palette.primary.dark,
    primaryLight: theme.palette.primary.light,
    secondary: theme.palette.secondary.main,
    accent: theme.palette.secondary.main,
    success: theme.palette.success.main,
    warning: '#D97706',
    error: '#DC2626',
    background: theme.palette.background.default,
    backgroundLight: isDark ? '#111827' : '#F1F5F9',
    surface: isDark ? '#1F2937' : '#F9FAFB',
    surfaceLight: isDark ? '#374151' : '#F3F4F6',
    text: theme.palette.text.primary,
    textSecondary: theme.palette.text.secondary,
    border: isDark ? '#374151' : alpha('#94A3B8', 0.3),
    cardBg: isDark ? '#1F2937' : '#FFFFFF',
  };
};

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.8;
  }
`;

const glow = keyframes`
  0%, 100% {
    box-shadow: 0 0 20px rgba(6, 95, 70, 0.3);
  }
  50% {
    box-shadow: 0 0 30px rgba(6, 95, 70, 0.5);
  }
`;

// Styled Components
const MainContainer = styled(Box)(({ theme }) => {
  const colors = getColors(theme);
  return {
    width: '100%',
    minHeight: '100vh',
    background: colors.background,
    margin: 0,
    padding: 0,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    '& .MuiContainer-root': {
      padding: '0 !important',
      margin: '0 !important',
      maxWidth: '100% !important',
    },
  };
});

const HeaderSection = styled(Box)(({ theme }) => {
  const colors = getColors(theme);
  return {
    background: colors.backgroundLight,
    borderRadius: 0,
    padding: 0,
    margin: 0,
    border: 'none',
    width: '100%',
    boxSizing: 'border-box',
  };
});

const StatsCard = styled(motion(Card))(({ theme, variant = 'default' }) => {
  const colors = getColors(theme);
  const isDark = theme.palette.mode === 'dark';

  const getGradient = () => {
    if (variant === 'primary') {
      return isDark
        ? `linear-gradient(135deg, ${alpha(colors.primary, 0.2)} 0%, ${alpha(colors.primaryLight, 0.1)} 100%)`
        : `linear-gradient(135deg, ${alpha(colors.primary, 0.15)} 0%, ${alpha(colors.primaryLight, 0.05)} 100%)`;
    } else if (variant === 'secondary') {
      return isDark
        ? `linear-gradient(135deg, ${alpha(colors.secondary, 0.15)} 0%, ${alpha(colors.primary, 0.1)} 100%)`
        : `linear-gradient(135deg, ${alpha(colors.secondary, 0.1)} 0%, ${alpha(colors.primary, 0.05)} 100%)`;
    }
    return colors.cardBg;
  };

  return {
    background: getGradient(),
    borderRadius: '16px',
    border: `1px solid ${alpha(colors.border, isDark ? 0.3 : 0.15)}`,
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    minHeight: '120px',
    position: 'relative',
    overflow: 'hidden',
    boxShadow: isDark
      ? `0 4px 20px ${alpha('#000', 0.3)}`
      : `0 4px 20px ${alpha(colors.primary, 0.08)}`,
    backdropFilter: 'blur(10px)',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '3px',
      background: variant === 'primary'
        ? `linear-gradient(90deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`
        : variant === 'secondary'
        ? `linear-gradient(90deg, ${colors.secondary} 0%, ${colors.primary} 100%)`
        : 'transparent',
      opacity: 0.8,
    },
    '& .MuiCardContent-root': {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      padding: '20px 24px !important',
      position: 'relative',
      zIndex: 1,
    },
  };
});

const SearchBar = styled(Paper)(({ theme }) => {
  const colors = getColors(theme);
  const isDark = theme.palette.mode === 'dark';
  return {
    display: 'flex',
    alignItems: 'center',
    background: colors.cardBg,
    borderRadius: '12px',
    padding: '12px 18px',
    border: `1px solid ${alpha(colors.border, isDark ? 0.3 : 0.2)}`,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: `0 2px 8px ${alpha('#000', isDark ? 0.2 : 0.04)}`,
    backdropFilter: 'blur(10px)',
    '&:hover': {
      background: alpha(colors.cardBg, isDark ? 0.9 : 0.95),
      borderColor: alpha(colors.primary, 0.3),
      boxShadow: `0 4px 12px ${alpha('#000', isDark ? 0.25 : 0.08)}`,
    },
    '&:focus-within': {
      borderColor: colors.primary,
      boxShadow: `0 0 0 3px ${alpha(colors.primary, 0.15)}, 0 4px 12px ${alpha('#000', isDark ? 0.25 : 0.08)}`,
      background: alpha(colors.cardBg, isDark ? 0.95 : 1),
    },
  };
});

const ModernButton = styled(Button)(({ theme, active }) => {
  const colors = getColors(theme);
  const isDark = theme.palette.mode === 'dark';

  return {
    borderRadius: '10px',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.875rem',
    padding: '10px 18px',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: 'none',
    border: `1px solid ${alpha(colors.border, isDark ? 0.3 : 0.2)}`,
    whiteSpace: 'nowrap',
    '&:hover': {
      boxShadow: `0 4px 12px ${alpha('#000', isDark ? 0.2 : 0.08)}`,
      transform: 'translateY(-1px)',
    },
    '&:active': {
      transform: 'translateY(0)',
    },
  };
});

const StyledTableContainer = styled(TableContainer)(({ theme }) => {
  const colors = getColors(theme);
  return {
    background: colors.cardBg,
    borderRadius: 0,
    border: 'none',
    boxShadow: 'none',
    width: '100%',
    maxWidth: '100%',
    margin: 0,
    padding: '0 32px 24px 32px',
    boxSizing: 'border-box',
  };
});

const StyledTableRow = styled(TableRow, {
  shouldForwardProp: (prop) => prop !== 'isCompleted'
})(({ theme, isCompleted }) => {
  const colors = getColors(theme);
  return {
    cursor: 'pointer',
    position: 'relative',
    zIndex: 0,
    transition: 'all 0.2s ease',
    borderLeft: isCompleted ? `3px solid ${colors.success}` : 'none',
    background: isCompleted ? alpha(colors.success, 0.02) : 'transparent',
    '&:hover': {
      background: isCompleted
        ? alpha(colors.success, 0.06)
        : alpha(colors.surfaceLight, 0.3),
    },
    '&.MuiTableRow-selected': {
      background: alpha(colors.primary, 0.1),
    },
  };
});

const StyledHeaderCell = styled(TableCell)(({ theme, clickable }) => {
  const colors = getColors(theme);
  const isDark = theme.palette.mode === 'dark';
  return {
    background: colors.surface,
    color: colors.text,
    fontWeight: 600,
    fontSize: '0.8125rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    padding: theme.spacing(2),
    borderTop: `2px solid ${alpha(colors.border, 0.4)}`,
    borderBottom: `2px solid ${alpha(colors.border, 0.4)}`,
    position: 'sticky',
    top: 0,
    zIndex: 1000,
    whiteSpace: 'nowrap',
    cursor: clickable ? 'pointer' : 'default',
    userSelect: 'none',
    transition: 'background-color 0.2s ease',
    boxShadow: `0 2px 8px ${alpha('#000', isDark ? 0.3 : 0.1)}`,
    backdropFilter: 'blur(10px)',
    '&:hover': clickable ? {
      backgroundColor: alpha(colors.primary, 0.08),
    } : {},
    '&:first-of-type': {
      borderLeft: `2px solid ${alpha(colors.border, 0.4)}`,
      borderTopLeftRadius: '8px',
      borderBottomLeftRadius: '8px',
    },
    '&:last-of-type': {
      borderRight: `2px solid ${alpha(colors.border, 0.4)}`,
      borderTopRightRadius: '8px',
      borderBottomRightRadius: '8px',
    },
  };
});

const StyledTableCell = styled(TableCell)(({ theme }) => {
  const colors = getColors(theme);
  return {
    color: colors.textSecondary,
    padding: theme.spacing(2),
    borderBottom: 'none',
  };
});

const BonusAmountText = styled(Typography)(({ theme }) => {
  const colors = getColors(theme);
  return {
    fontSize: '1.125rem',
    fontWeight: 700,
    color: colors.primary,
  };
});

const DifficultyChip = styled(Chip)(({ difficulty, theme }) => {
  const colors = getColors(theme);
  const colorMap = {
    Easy: colors.success,
    Medium: colors.warning,
    Hard: colors.error,
  };
  const color = colorMap[difficulty] || colors.warning;

  return {
    backgroundColor: alpha(color, 0.15),
    color: color,
    border: 'none',
    fontWeight: 600,
    fontSize: '0.75rem',
  };
});

const SuccessRateChip = styled(Chip)(({ rate, theme }) => {
  const colors = getColors(theme);
  const getColor = (rate) => {
    if (rate >= 70) return colors.success;
    if (rate >= 40) return colors.warning;
    return colors.error;
  };
  const color = getColor(rate);

  return {
    backgroundColor: alpha(color, 0.15),
    color: color,
    border: 'none',
    fontWeight: 600,
    fontSize: '0.75rem',
  };
});

const BankCard = styled(motion(Card), {
  shouldForwardProp: (prop) => prop !== 'isCompleted'
})(({ theme, isCompleted }) => {
  const colors = getColors(theme);
  return {
    background: isCompleted ? alpha(colors.success, 0.02) : colors.cardBg,
    borderRadius: '8px',
    border: isCompleted ? `2px solid ${colors.success}` : 'none',
    cursor: 'pointer',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'hidden',
    boxShadow: isCompleted
      ? `0 2px 12px ${alpha(colors.success, 0.15)}`
      : 'none',
    '& .MuiCardContent-root': {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      padding: theme.spacing(3),
    },
  };
});

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

const WebBankTable = ({
  banks = [],
  onBankClick,
  onBookmarkToggle,
  onArchiveToggle,
  onSearch,
  searchValue = '',
  bookmarkedBanks = [],
  archivedBanks = [],
  loading = false,
  isAdmin = false,
  onEditBank,
  onDeleteBanks,
  selectedState,
  onStateClick,
  totalEarned = 0,
  completedCount = 0,
}) => {
  const theme = useTheme();
  const { user, userData } = useAuth();
  const colors = getColors(theme); // Theme-aware colors
  const navigate = useNavigate();
  const location = useLocation();

  // Global state using use-s
  const [showBookmarked, setShowBookmarked] = useS(store.showBookmarked);
  const [showArchived, setShowArchived] = useS(store.showArchived);
  const [filterByHomeState, setFilterByHomeState] = useS(store.filterByHomeState);
  const [viewMode, setViewMode] = useS(store.viewMode);
  const [earnedAmount, setEarnedAmount] = useS(store.earnedAmount);
  const [availableAmount, setAvailableAmount] = useS(store.availableAmount);
  const [completedBonusCount, setCompletedBonusCount] = useS(store.completedBonusCount);
  const [completedBankIds, setCompletedBankIds] = useS(store.completedBankIds);
  const [inProgressBankIds, setInProgressBankIds] = useS(store.inProgressBankIds);
  const [selectedDifficulty, setSelectedDifficulty] = useS(store.selectedDifficulty);
  const [sortBy, setSortBy] = useS(store.sortBy);
  const [sortOrder, setSortOrder] = useS(store.sortOrder);
  const [page, setPage] = useS(store.currentPage);
  const [rowsPerPage, setRowsPerPage] = useS(store.rowsPerPage);

  // Local state
  const [selectedBanks, setSelectedBanks] = useS([]);
  const [sortAnchorEl, setSortAnchorEl] = useS(null);
  const [filterAnchorEl, setFilterAnchorEl] = useS(null);
  const [locationAnchorEl, setLocationAnchorEl] = useS(null);

  // Derive sortConfig from sortBy and sortOrder
  const sortConfig = useMemo(() => ({
    key: sortBy,
    direction: sortOrder
  }), [sortBy, sortOrder]);

  // Reset to first page when filters change
  useEffect(() => {
    setPage(0);
  }, [searchValue, showBookmarked, showArchived, selectedDifficulty, selectedState, sortConfig, filterByHomeState]);

  useEffect(() => {
    const fetchBonusData = async () => {
      if (!user?.uid) {
        setEarnedAmount(0);
        setAvailableAmount(0);
        setCompletedBonusCount(0);
        setCompletedBankIds([]);
        setInProgressBankIds([]);
        return;
      }

      try {
        const userBonusesRef = collection(db, 'userBonuses');
        const completedBonusesQuery = query(userBonusesRef,
          where('userId', '==', user.uid),
          where('status', '==', 'completed'),
          where('isPaidOut', '==', true)
        );
        const completedBonusesSnapshot = await getDocs(completedBonusesQuery);

        console.log('Completed bonuses count:', completedBonusesSnapshot.docs.length);
        setCompletedBonusCount(completedBonusesSnapshot.docs.length);

        const totalEarned = completedBonusesSnapshot.docs.reduce((total, doc) => {
          const data = doc.data();
          const amount = parseFloat(data.bonusAmount) || 0;
          console.log('Bonus amount:', amount, 'Bank ID:', data.bankId);
          return total + amount;
        }, 0);
        setEarnedAmount(totalEarned);

        const banksRef = collection(db, 'banks');
        const availableBanksQuery = query(banksRef);
        const availableBanksSnapshot = await getDocs(availableBanksQuery);

        const completedIds = completedBonusesSnapshot.docs.map(doc => doc.data().bankId);
        setCompletedBankIds(completedIds);

        // Fetch in-progress bonuses
        const inProgressBonusesQuery = query(userBonusesRef,
          where('userId', '==', user.uid)
        );
        const inProgressBonusesSnapshot = await getDocs(inProgressBonusesQuery);
        const inProgressIds = inProgressBonusesSnapshot.docs
          .filter(doc => doc.data().status !== 'completed')
          .map(doc => doc.data().bankId);
        setInProgressBankIds(inProgressIds);

        const totalAvailable = availableBanksSnapshot.docs.reduce((total, doc) => {
          const data = doc.data();
          if (!completedIds.includes(doc.id)) {
            const amount = parseFloat(data.amount) || 0;
            return total + amount;
          }
          return total;
        }, 0);

        setAvailableAmount(totalAvailable);
      } catch (error) {
        console.error('Error fetching bonus data:', error);
        setEarnedAmount(0);
        setAvailableAmount(0);
        setCompletedBonusCount(0);
        setCompletedBankIds([]);
        setInProgressBankIds([]);
      }
    };

    fetchBonusData();
  }, [user]);

  const filteredBanks = useMemo(() => {
    let result = [...banks];

    if (searchValue) {
      result = result.filter(bank =>
        bank.name.toLowerCase().includes(searchValue.toLowerCase())
      );
    }

    if (showBookmarked) {
      result = result.filter(bank => bookmarkedBanks.includes(bank.id));
    }

    // Filter archived banks
    if (showArchived) {
      // When showArchived is true, ONLY show archived banks
      result = result.filter(bank => archivedBanks.includes(bank.id));
    } else {
      // When showArchived is false, exclude archived banks from main view
      result = result.filter(bank => !archivedBanks.includes(bank.id));
    }

    if (selectedDifficulty) {
      result = result.filter(bank =>
        (bank.difficulty || 'Medium').toLowerCase() === selectedDifficulty.toLowerCase()
      );
    }

    if (selectedState) {
      result = result.filter(bank => bank.states?.includes(selectedState));
    }

    // Filter by home state if enabled
    if (filterByHomeState && userData?.homeState) {
      result = result.filter(bank => {
        const bankStates = bank.states?.map(s => s.toUpperCase()) || [];
        return bankStates.includes('NATIONWIDE') || bankStates.includes(userData.homeState.toUpperCase());
      });
    }

    return result;
  }, [banks, searchValue, showBookmarked, bookmarkedBanks, showArchived, archivedBanks, selectedState, selectedDifficulty, filterByHomeState, userData]);

  const sortedBanks = useMemo(() => {
    // Sort function
    const sortBanks = (banks) => {
      if (!sortConfig.key) return banks;

      return [...banks].sort((a, b) => {
        switch (sortConfig.key) {
          case 'name':
            return sortConfig.direction === 'asc'
              ? a.name.localeCompare(b.name)
              : b.name.localeCompare(a.name);
          case 'bonus':
            const aAmount = parseFloat(a.amount || 0);
            const bAmount = parseFloat(b.amount || 0);
            return sortConfig.direction === 'asc'
              ? aAmount - bAmount
              : bAmount - aAmount;
          case 'difficulty':
            const difficultyOrder = { 'Easy': 1, 'Medium': 2, 'Hard': 3 };
            const aDiff = difficultyOrder[a.difficulty] || 2;
            const bDiff = difficultyOrder[b.difficulty] || 2;
            return sortConfig.direction === 'asc'
              ? aDiff - bDiff
              : bDiff - aDiff;
          case 'successRate':
            const aRate = a.successRate || 0;
            const bRate = b.successRate || 0;
            return sortConfig.direction === 'asc'
              ? aRate - bRate
              : bRate - aRate;
          case 'expiration':
            const today = new Date().getTime();
            // Calculate days until expiration from today
            const aDaysUntil = a.expiration ? (new Date(a.expiration).getTime() - today) : Infinity;
            const bDaysUntil = b.expiration ? (new Date(b.expiration).getTime() - today) : Infinity;
            // asc = soonest expiration first, desc = farthest expiration first
            return sortConfig.direction === 'asc'
              ? aDaysUntil - bDaysUntil
              : bDaysUntil - aDaysUntil;
          case 'status':
            // Determine status: 1 = In Progress, 0 = Not Started
            const aStatus = inProgressBankIds.includes(a.id) ? 1 : 0;
            const bStatus = inProgressBankIds.includes(b.id) ? 1 : 0;
            // asc = Not Started first, desc = In Progress first
            return sortConfig.direction === 'asc'
              ? aStatus - bStatus
              : bStatus - aStatus;
          default:
            return 0;
        }
      });
    };

    // If user explicitly selected a sort, apply it to all banks without prioritizing bookmarked
    if (sortConfig.key) {
      return sortBanks(filteredBanks);
    }

    // If no sort selected, show bookmarked banks first
    const bookmarked = filteredBanks.filter(bank => bookmarkedBanks.includes(bank.id));
    const nonBookmarked = filteredBanks.filter(bank => !bookmarkedBanks.includes(bank.id));
    return [...bookmarked, ...nonBookmarked];
  }, [filteredBanks, sortConfig, bookmarkedBanks]);

  const handleSortClick = (event) => setSortAnchorEl(event.currentTarget);
  const handleFilterClick = (event) => setFilterAnchorEl(event.currentTarget);
  const handleLocationClick = (event) => setLocationAnchorEl(event.currentTarget);
  const handleMenuClose = () => {
    setSortAnchorEl(null);
    setFilterAnchorEl(null);
    setLocationAnchorEl(null);
  };

  const handleSortOption = (option) => {
    const newDirection = sortConfig.key === option && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortBy(option);
    setSortOrder(newDirection);
    handleMenuClose();
  };

  const handleFilterOption = (option) => {
    setSelectedDifficulty(selectedDifficulty === option ? null : option);
    handleMenuClose();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Paginated banks for table view
  const paginatedBanks = useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return sortedBanks.slice(startIndex, endIndex);
  }, [sortedBanks, page, rowsPerPage]);

  // Calculate total available bonuses
  const totalAvailable = useMemo(() => {
    return banks.reduce((total, bank) => total + (Number(bank.amount) || 0), 0);
  }, [banks]);

  const renderGridView = () => (
    <>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        style={{ width: '100%' }}
        className="bank-table-section bank-grid-section"
      >
        <Grid container spacing={3} sx={{ m: 0, width: '100%', p: '0 32px 24px 32px' }}>
          {paginatedBanks.map((bank, index) => {
            const isCompleted = completedBankIds.includes(bank.id);
            return (
          <Grid item xs={12} sm={6} md={4} lg={3} key={bank.id} sx={{ display: 'flex' }}>
            <BankCard
              className={index === 0 ? 'bank-card-example' : ''}
              onClick={() => onBankClick(bank)}
              isCompleted={isCompleted}
              variants={cardVariants}
              whileHover={{
                scale: 1.03,
                y: -8,
                boxShadow: isCompleted
                  ? `0 12px 24px ${alpha(colors.success, 0.3)}`
                  : `0 12px 24px ${alpha(colors.primary, 0.15)}`,
                transition: {
                  type: 'spring',
                  stiffness: 400,
                  damping: 17
                }
              }}
              whileTap={{ scale: 0.98 }}
              style={{ width: '100%' }}
            >
              <CardContent>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Typography variant="h6" sx={{ color: colors.text, fontWeight: 700 }}>
                        {bank.name}
                      </Typography>
                      {bank.url && (
                        <Button
                          size="small"
                          variant="outlined"
                          endIcon={<OpenInNewIcon sx={{ fontSize: '0.875rem' }} />}
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(bank.url, '_blank', 'noopener,noreferrer');
                          }}
                          className={index === 2 ? 'bonus-link-button' : ''}
                          sx={{
                            alignSelf: 'flex-start',
                            fontSize: '0.75rem',
                            padding: '4px 10px',
                            borderColor: alpha(colors.primary, 0.3),
                            color: colors.primary,
                            '&:hover': {
                              borderColor: colors.primary,
                              backgroundColor: alpha(colors.primary, 0.05),
                            }
                          }}
                        >
                          Bonus Link
                        </Button>
                      )}
                    </Box>
                    <Stack direction="row" spacing={0.5} className={index === 0 ? 'bookmark-section' : ''}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onBookmarkToggle(bank);
                        }}
                      >
                        {bookmarkedBanks.includes(bank.id) ?
                          <BookmarkIcon sx={{ color: colors.primary }} /> :
                          <BookmarkBorderIcon sx={{ color: colors.textSecondary }} />}
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onArchiveToggle(bank);
                        }}
                      >
                        {archivedBanks.includes(bank.id) ?
                          <UnarchiveIcon sx={{ color: colors.textSecondary }} /> :
                          <ArchiveIcon sx={{ color: colors.textSecondary }} />}
                      </IconButton>
                    </Stack>
                  </Box>

                  <BonusAmountText>
                    {formatCurrency(bank.bonusAmount || bank.amount)}
                  </BonusAmountText>

                  <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                    {completedBankIds.includes(bank.id) ? (
                      <Chip
                        label="Completed"
                        size="small"
                        icon={<CheckCircleIcon sx={{ fontSize: '0.875rem !important' }} />}
                        sx={{
                          backgroundColor: alpha(colors.success, 0.15),
                          color: colors.success,
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          border: 'none',
                          '& .MuiChip-icon': {
                            color: colors.success,
                          }
                        }}
                      />
                    ) : inProgressBankIds.includes(bank.id) ? (
                      <Chip
                        label="In Progress"
                        size="small"
                        sx={{
                          backgroundColor: alpha(colors.primary, 0.15),
                          color: colors.primary,
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          border: 'none',
                        }}
                      />
                    ) : (
                      <Chip
                        label="Not Started"
                        size="small"
                        sx={{
                          backgroundColor: alpha(colors.textSecondary, 0.1),
                          color: colors.textSecondary,
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          border: 'none',
                        }}
                      />
                    )}
                    <DifficultyChip
                      difficulty={bank.difficulty || 'Medium'}
                      label={bank.difficulty || 'Medium'}
                      size="small"
                    />
                    {(bank.totalAttempts > 0 || bank.successfulCompletions > 0 || bank.failedCompletions > 0) && (
                      <SuccessRateChip
                        rate={bank.successRate || 0}
                        label={`${bank.successRate || 0}%`}
                        size="small"
                      />
                    )}
                  </Stack>

                  <Stack spacing={1}>
                    {bank.directDepositAmount && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TrendingUpIcon sx={{ fontSize: 16, color: colors.textSecondary }} />
                        <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                          DD: {formatCurrency(bank.directDepositAmount)}
                        </Typography>
                      </Box>
                    )}
                    {(bank.days || bank.timeframe) && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AccessTimeIcon sx={{ fontSize: 16, color: colors.textSecondary }} />
                        <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                          {bank.days || bank.timeframe} days
                        </Typography>
                      </Box>
                    )}
                    {bank.states && bank.states.length > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocationOnIcon sx={{ fontSize: 16, color: colors.textSecondary }} />
                        <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                          {bank.states.length} states
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Stack>
              </CardContent>
            </BankCard>
          </Grid>
        );
          })}
      </Grid>
    </motion.div>
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
      <TablePagination
        component="div"
        count={sortedBanks.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50, 100]}
        sx={{
          color: colors.text,
          background: alpha(colors.cardBg, 0.8),
          borderRadius: '16px',
          padding: '12px',
          border: `1px solid ${alpha(colors.primary, 0.2)}`,
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
            color: colors.textSecondary,
            fontWeight: 600,
          },
          '& .MuiTablePagination-select': {
            color: colors.text,
            background: alpha(colors.primary, 0.1),
            borderRadius: '8px',
            padding: '4px 8px',
          },
          '& .MuiTablePagination-actions': {
            color: colors.text,
          },
          '& .MuiIconButton-root': {
            color: colors.primaryLight,
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: alpha(colors.primary, 0.2),
              transform: 'scale(1.1)',
            },
          },
        }}
      />
    </Box>
  </>
  );

  const renderTableView = () => (
    <StyledTableContainer className="bank-table-section">
      <Table stickyHeader sx={{ width: '100%', tableLayout: 'auto' }} component={motion.table}>
        <TableHead component={motion.thead}>
          <TableRow component={motion.tr}>
            <StyledHeaderCell
              clickable
              onClick={() => handleSortOption('name')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Bank Name
                {sortConfig.key === 'name' && (
                  sortConfig.direction === 'asc' ? <ArrowDropUpIcon sx={{ fontSize: 20 }} /> : <ArrowDropDownIcon sx={{ fontSize: 20 }} />
                )}
              </Box>
            </StyledHeaderCell>
            <StyledHeaderCell
              clickable
              onClick={() => handleSortOption('bonus')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Bonus
                {sortConfig.key === 'bonus' && (
                  sortConfig.direction === 'asc' ? <ArrowDropUpIcon sx={{ fontSize: 20 }} /> : <ArrowDropDownIcon sx={{ fontSize: 20 }} />
                )}
              </Box>
            </StyledHeaderCell>
            <StyledHeaderCell>Required DD</StyledHeaderCell>
            <StyledHeaderCell
              clickable
              onClick={() => handleSortOption('status')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Status
                {sortConfig.key === 'status' && (
                  sortConfig.direction === 'asc' ? <ArrowDropUpIcon sx={{ fontSize: 20 }} /> : <ArrowDropDownIcon sx={{ fontSize: 20 }} />
                )}
              </Box>
            </StyledHeaderCell>
            <StyledHeaderCell
              clickable
              onClick={() => handleSortOption('expiration')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Expiration
                {sortConfig.key === 'expiration' && (
                  sortConfig.direction === 'asc' ? <ArrowDropUpIcon sx={{ fontSize: 20 }} /> : <ArrowDropDownIcon sx={{ fontSize: 20 }} />
                )}
              </Box>
            </StyledHeaderCell>
            <StyledHeaderCell>Location</StyledHeaderCell>
            <StyledHeaderCell>Actions</StyledHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <StyledTableCell colSpan={7} align="center">
                <CircularProgress sx={{ color: colors.primary }} />
              </StyledTableCell>
            </TableRow>
          ) : paginatedBanks.length > 0 ? (
            paginatedBanks.map((bank, index) => {
              const isCompleted = completedBankIds.includes(bank.id);
              return (
                <StyledTableRow
                  key={bank.id}
                  onClick={() => onBankClick(bank)}
                  isCompleted={isCompleted}
                  className={index === 2 ? 'bank-row-example' : ''}
                >
                  <StyledTableCell sx={{ minWidth: '300px' }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Typography sx={{ color: colors.text, fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {bank.name}
                      </Typography>
                      <DifficultyChip
                        difficulty={bank.difficulty || 'Medium'}
                        label={bank.difficulty || 'Medium'}
                        size="small"
                      />
                      {(bank.totalAttempts > 0 || bank.successfulCompletions > 0 || bank.failedCompletions > 0) && (
                        <SuccessRateChip
                          rate={bank.successRate || 0}
                          label={`${bank.successRate || 0}%`}
                          size="small"
                        />
                      )}
                      {bank.url && (
                        <Button
                          size="small"
                          variant="outlined"
                          endIcon={<OpenInNewIcon sx={{ fontSize: '0.875rem' }} />}
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(bank.url, '_blank', 'noopener,noreferrer');
                          }}
                          className={index === 2 ? 'bonus-link-button' : ''}
                          sx={{
                            fontSize: '0.75rem',
                            padding: '4px 10px',
                            borderColor: alpha(colors.primary, 0.3),
                            color: colors.primary,
                            '&:hover': {
                              borderColor: colors.primary,
                              backgroundColor: alpha(colors.primary, 0.05),
                            }
                          }}
                        >
                          Bonus Link
                        </Button>
                      )}
                    </Stack>
                  </StyledTableCell>
                  <StyledTableCell>
                    <BonusAmountText>
                      {formatCurrency(bank.bonusAmount || bank.amount)}
                    </BonusAmountText>
                  </StyledTableCell>
                  <StyledTableCell>
                    {bank.directDepositAmount ? formatCurrency(bank.directDepositAmount) : 'N/A'}
                  </StyledTableCell>
                  <StyledTableCell>
                    {completedBankIds.includes(bank.id) ? (
                      <Chip
                        label="Completed"
                        size="small"
                        icon={<CheckCircleIcon sx={{ fontSize: '0.875rem !important' }} />}
                        sx={{
                          backgroundColor: alpha(colors.success, 0.15),
                          color: colors.success,
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          '& .MuiChip-icon': {
                            color: colors.success,
                          }
                        }}
                      />
                    ) : inProgressBankIds.includes(bank.id) ? (
                      <Chip
                        label="In Progress"
                        size="small"
                        sx={{
                          backgroundColor: alpha(colors.primary, 0.15),
                          color: colors.primary,
                          fontWeight: 600,
                          fontSize: '0.75rem',
                        }}
                      />
                    ) : (
                      <Chip
                        label="Not Started"
                        size="small"
                        sx={{
                          backgroundColor: alpha(colors.textSecondary, 0.1),
                          color: colors.textSecondary,
                          fontWeight: 600,
                          fontSize: '0.75rem',
                        }}
                      />
                    )}
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography sx={{ color: bank.expiration ? colors.text : colors.textSecondary, fontSize: '0.875rem' }}>
                      {bank.expiration ? (() => {
                        // Format date without timezone conversion
                        if (typeof bank.expiration === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(bank.expiration)) {
                          // Already in YYYY-MM-DD format, just format for display
                          const [year, month, day] = bank.expiration.split('-');
                          return new Date(year, month - 1, day).toLocaleDateString();
                        }
                        // Parse as UTC to avoid timezone issues
                        const date = new Date(bank.expiration);
                        return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()).toLocaleDateString();
                      })() : 'N/A'}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                      {bank.states?.slice(0, 2).map((state, idx) => (
                        <Chip
                          key={idx}
                          label={state}
                          size="small"
                          sx={{
                            backgroundColor: alpha(colors.primary, 0.15),
                            color: colors.text,
                            fontSize: '0.7rem',
                            border: `1px solid ${alpha(colors.primary, 0.3)}`,
                            fontWeight: 600,
                          }}
                        />
                      ))}
                      {bank.states?.length > 2 && (
                        <Chip
                          label={`+${bank.states.length - 2}`}
                          size="small"
                          sx={{
                            backgroundColor: alpha(colors.secondary, 0.15),
                            color: colors.text,
                            fontSize: '0.7rem',
                            border: `1px solid ${alpha(colors.secondary, 0.3)}`,
                            fontWeight: 600,
                          }}
                        />
                      )}
                    </Stack>
                  </StyledTableCell>
                  <StyledTableCell onClick={(e) => e.stopPropagation()}>
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title={bookmarkedBanks.includes(bank.id) ? "Remove bookmark" : "Bookmark"}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onBookmarkToggle(bank);
                          }}
                        >
                          {bookmarkedBanks.includes(bank.id) ?
                            <BookmarkIcon sx={{ color: colors.primary }} /> :
                            <BookmarkBorderIcon sx={{ color: colors.textSecondary }} />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={archivedBanks.includes(bank.id) ? "Unarchive" : "Archive"}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onArchiveToggle(bank);
                          }}
                        >
                          {archivedBanks.includes(bank.id) ?
                            <UnarchiveIcon sx={{ color: colors.textSecondary }} /> :
                            <ArchiveIcon sx={{ color: colors.textSecondary }} />}
                        </IconButton>
                      </Tooltip>
                      {(isAdmin || bank.createdBy === user?.uid) && (
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onEditBank(bank);
                            }}
                          >
                            <EditIcon sx={{ color: colors.textSecondary }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </StyledTableCell>
                </StyledTableRow>
              );
            })
          ) : (
            <TableRow>
              <StyledTableCell colSpan={7} align="center">
                <Typography sx={{ color: colors.textSecondary, py: 4 }}>
                  No banks found
                </Typography>
              </StyledTableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={sortedBanks.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50, 100]}
        sx={{
          color: colors.text,
          borderTop: `1px solid ${alpha(colors.primary, 0.2)}`,
          background: alpha(colors.cardBg, 0.5),
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
            color: colors.textSecondary,
            fontWeight: 600,
          },
          '& .MuiTablePagination-select': {
            color: colors.text,
            background: alpha(colors.primary, 0.1),
            borderRadius: '8px',
            padding: '4px 8px',
          },
          '& .MuiTablePagination-actions': {
            color: colors.text,
          },
          '& .MuiIconButton-root': {
            color: colors.primaryLight,
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: alpha(colors.primary, 0.2),
              transform: 'scale(1.1)',
            },
          },
        }}
      />
    </StyledTableContainer>
  );

  return (
    <MainContainer>
      {/* Compact Combined Header */}
      <HeaderSection>
        <Box sx={{
          background: alpha(colors.cardBg, 0.6),
          borderRadius: 0,
          padding: '20px',
          border: 'none',
          borderBottom: `1px solid ${alpha(colors.border, theme.palette.mode === 'dark' ? 0.2 : 0.15)}`,
          backdropFilter: 'blur(10px)',
          boxShadow: 'none',
        }}>
          {/* Stats Row - Compact */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="bonus-stats-section"
          >
            <Stack direction="row" spacing={3} sx={{ mb: 2.5 }} flexWrap="wrap">
              {/* Total Earned - Compact */}
              <motion.div variants={statsVariants} style={{ display: 'flex', minWidth: 'fit-content' }}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 'fit-content' }}>
                <Box sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: `linear-gradient(135deg, ${alpha(colors.primary, 0.15)} 0%, ${alpha(colors.primaryLight, 0.08)} 100%)`,
                border: `1px solid ${alpha(colors.primary, 0.2)}`,
              }}>
                <AccountBalanceWalletIcon sx={{ color: colors.primary, fontSize: 20 }} />
              </Box>
              <Box>
                <Typography variant="caption" sx={{
                  color: alpha(colors.textSecondary, 0.8),
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  display: 'block',
                  lineHeight: 1,
                }}>
                  Earned
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mt: 0.5 }}>
                  <Typography variant="h5" sx={{
                    color: colors.primary,
                    fontWeight: 700,
                    fontSize: '1.25rem',
                    lineHeight: 1,
                  }}>
                    {formatCurrency(earnedAmount)}
                  </Typography>
                  <Typography variant="caption" sx={{
                    color: alpha(colors.textSecondary, 0.6),
                    fontSize: '0.7rem',
                  }}>
                    / {formatCurrency(availableAmount)}
                  </Typography>
                </Box>
              </Box>
            </Stack>
            </motion.div>

            {/* Completed - Compact */}
            <motion.div variants={statsVariants} style={{ display: 'flex', minWidth: 'fit-content' }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 'fit-content' }}>
              <Box sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: `linear-gradient(135deg, ${alpha(colors.success, 0.15)} 0%, ${alpha(colors.success, 0.08)} 100%)`,
                border: `1px solid ${alpha(colors.success, 0.2)}`,
              }}>
                <CheckCircleIcon sx={{ color: colors.success, fontSize: 20 }} />
              </Box>
              <Box>
                <Typography variant="caption" sx={{
                  color: alpha(colors.textSecondary, 0.8),
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  display: 'block',
                  lineHeight: 1,
                }}>
                  Completed
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mt: 0.5 }}>
                  <Typography variant="h5" sx={{
                    color: colors.text,
                    fontWeight: 700,
                    fontSize: '1.25rem',
                    lineHeight: 1,
                  }}>
                    {completedBonusCount}
                  </Typography>
                  <Typography variant="caption" sx={{
                    color: alpha(colors.textSecondary, 0.6),
                    fontSize: '0.7rem',
                  }}>
                    / {banks.length}
                  </Typography>
                </Box>
              </Box>
            </Stack>
            </motion.div>

            {/* Available - Compact */}
            <motion.div variants={statsVariants} style={{ display: 'flex', minWidth: 'fit-content' }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 'fit-content' }}>
              <Box sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: `linear-gradient(135deg, ${alpha(colors.secondary, 0.12)} 0%, ${alpha(colors.secondary, 0.05)} 100%)`,
                border: `1px solid ${alpha(colors.secondary, 0.2)}`,
              }}>
                <LocalOfferIcon sx={{ color: colors.secondary, fontSize: 20 }} />
              </Box>
              <Box>
                <Typography variant="caption" sx={{
                  color: alpha(colors.textSecondary, 0.8),
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  display: 'block',
                  lineHeight: 1,
                }}>
                  Available
                </Typography>
                <Typography variant="h5" sx={{
                  color: colors.text,
                  fontWeight: 700,
                  fontSize: '1.25rem',
                  lineHeight: 1,
                  mt: 0.5,
                }}>
                  {sortedBanks.length}
                </Typography>
              </Box>
            </Stack>
            </motion.div>
          </Stack>
        </motion.div>

          {/* Divider */}
          <Divider sx={{ my: 2, borderColor: alpha(colors.border, 0.3) }} />

          {/* Search and Controls - Combined */}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 0 }} className="search-filter-section">
            <SearchBar sx={{ flex: 1 }}>
              <SearchIcon sx={{ color: colors.primary, mr: 1.5, fontSize: 22 }} />
              <InputBase
                sx={{
                  flex: 1,
                  color: colors.text,
                  fontWeight: 500,
                  '&::placeholder': {
                    color: colors.textSecondary,
                    opacity: 0.7,
                  }
                }}
                placeholder="Search for bank bonuses..."
                value={searchValue}
                onChange={(e) => onSearch(e.target.value)}
              />
              {searchValue && (
                <IconButton
                  size="small"
                  onClick={() => onSearch('')}
                  sx={{
                    color: colors.textSecondary,
                    padding: '4px',
                    '&:hover': {
                      background: alpha(colors.primary, 0.1),
                      color: colors.primary,
                    }
                  }}
                >
                  ×
                </IconButton>
              )}
            </SearchBar>

            <Stack direction="row" spacing={1.5} flexWrap="wrap" alignItems="center">
              <Tooltip title={viewMode === 'table' ? "Switch to grid view" : "Switch to table view"} arrow>
                <ModernButton
                  variant="text"
                  onClick={() => setViewMode(viewMode === 'table' ? 'grid' : 'table')}
                  sx={{
                    minWidth: '44px',
                    padding: '10px',
                    color: colors.text,
                    background: alpha(colors.surfaceLight, 0.5),
                    '&:hover': {
                      background: alpha(colors.primary, 0.15),
                      color: colors.primary,
                    },
                  }}
                >
                  {viewMode === 'table' ? <ViewModuleIcon fontSize="small" /> : <ViewListIcon fontSize="small" />}
                </ModernButton>
              </Tooltip>

              <Badge
                badgeContent={bookmarkedBanks.length}
                color="primary"
                sx={{
                  '& .MuiBadge-badge': {
                    background: colors.primary,
                    color: '#fff',
                    fontWeight: 700,
                  }
                }}
              >
                <ModernButton
                  variant={showBookmarked ? "contained" : "outlined"}
                  onClick={() => setShowBookmarked(!showBookmarked)}
                  startIcon={<BookmarkIcon sx={{ fontSize: 18 }} />}
                  sx={{
                    color: showBookmarked ? '#fff' : colors.text,
                    background: showBookmarked
                      ? `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`
                      : alpha(colors.cardBg, 0.5),
                    borderColor: showBookmarked ? 'transparent' : alpha(colors.border, 0.3),
                    '&:hover': {
                      background: showBookmarked
                        ? `linear-gradient(135deg, ${colors.primaryLight} 0%, ${colors.primary} 100%)`
                        : alpha(colors.primary, 0.1),
                      borderColor: showBookmarked ? 'transparent' : colors.primary,
                    },
                  }}
                >
                  Saved
                </ModernButton>
              </Badge>

              <Badge
                badgeContent={archivedBanks.length}
                color="default"
                sx={{
                  '& .MuiBadge-badge': {
                    background: alpha(colors.textSecondary, 0.8),
                    color: '#fff',
                    fontWeight: 700,
                  }
                }}
              >
                <Tooltip title={showArchived ? "Back to main view" : "View archived bonuses"} arrow>
                  <ModernButton
                    variant={showArchived ? "contained" : "outlined"}
                    onClick={() => setShowArchived(!showArchived)}
                    startIcon={showArchived ? <UnarchiveIcon sx={{ fontSize: 18 }} /> : <ArchiveIcon sx={{ fontSize: 18 }} />}
                    sx={{
                      color: showArchived ? '#fff' : colors.text,
                      background: showArchived
                        ? alpha(colors.textSecondary, 0.8)
                        : alpha(colors.cardBg, 0.5),
                      borderColor: showArchived ? 'transparent' : alpha(colors.border, 0.3),
                      '&:hover': {
                        background: showArchived
                          ? alpha(colors.textSecondary, 0.9)
                          : alpha(colors.textSecondary, 0.1),
                        borderColor: showArchived ? 'transparent' : colors.textSecondary,
                      },
                    }}
                  >
                    {showArchived ? 'Back to All' : 'Archived'}
                  </ModernButton>
                </Tooltip>
              </Badge>

              {userData?.homeState && (
                <Tooltip title={filterByHomeState ? `Showing bonuses for ${userData.homeState} and Nationwide` : 'Filter by home state disabled'}>
                  <ModernButton
                    variant={filterByHomeState ? "contained" : "outlined"}
                    onClick={() => setFilterByHomeState(!filterByHomeState)}
                    startIcon={<HomeIcon sx={{ fontSize: 18 }} />}
                    sx={{
                      color: filterByHomeState ? '#fff' : colors.text,
                      background: filterByHomeState
                        ? `linear-gradient(135deg, ${colors.success} 0%, ${alpha(colors.success, 0.8)} 100%)`
                        : alpha(colors.cardBg, 0.5),
                      borderColor: filterByHomeState ? 'transparent' : alpha(colors.border, 0.3),
                      '&:hover': {
                        background: filterByHomeState
                          ? `linear-gradient(135deg, ${alpha(colors.success, 0.9)} 0%, ${alpha(colors.success, 0.7)} 100%)`
                          : alpha(colors.success, 0.1),
                        borderColor: filterByHomeState ? 'transparent' : colors.success,
                      },
                    }}
                  >
                    {userData.homeState}
                  </ModernButton>
                </Tooltip>
              )}

              <Badge
                badgeContent={sortConfig.key ? '●' : 0}
                color="primary"
                variant="dot"
                sx={{
                  '& .MuiBadge-dot': {
                    background: colors.primary,
                  }
                }}
              >
                <ModernButton
                  variant="outlined"
                  startIcon={<SortIcon sx={{ fontSize: 18 }} />}
                  endIcon={sortConfig.key && (sortConfig.direction === 'asc' ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />)}
                  onClick={handleSortClick}
                  sx={{
                    color: colors.text,
                    background: alpha(colors.cardBg, 0.5),
                    borderColor: alpha(colors.border, 0.3),
                    '&:hover': {
                      background: alpha(colors.primary, 0.1),
                      borderColor: colors.primary,
                    },
                  }}
                >
                  Sort
                </ModernButton>
              </Badge>

              <Badge
                badgeContent={selectedDifficulty ? '●' : 0}
                color="primary"
                variant="dot"
                sx={{
                  '& .MuiBadge-dot': {
                    background: colors.warning,
                  }
                }}
              >
                <ModernButton
                  variant="outlined"
                  startIcon={<FilterListIcon sx={{ fontSize: 18 }} />}
                  onClick={handleFilterClick}
                  sx={{
                    color: colors.text,
                    background: alpha(colors.cardBg, 0.5),
                    borderColor: alpha(colors.border, 0.3),
                    '&:hover': {
                      background: alpha(colors.primary, 0.1),
                      borderColor: colors.primary,
                    },
                  }}
                >
                  Difficulty
                </ModernButton>
              </Badge>
            </Stack>
          </Stack>

          {/* Active Filters */}
          {(selectedDifficulty || selectedState) && (
            <Fade in timeout={300}>
              <Box sx={{
                mt: 2,
                p: 2,
                background: alpha(colors.cardBg, 0.3),
                borderRadius: '12px',
                border: `1px solid ${alpha(colors.border, 0.2)}`,
              }}>
              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                <Typography variant="caption" sx={{
                  color: colors.textSecondary,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  fontSize: '0.6875rem',
                  letterSpacing: '0.1em',
                }}>
                  Active Filters:
                </Typography>
                {selectedDifficulty && (
                  <Chip
                    label={selectedDifficulty}
                    onDelete={() => setSelectedDifficulty(null)}
                    size="small"
                    sx={{
                      backgroundColor: alpha(colors.warning, 0.15),
                      color: colors.text,
                      borderRadius: '8px',
                      border: `1px solid ${alpha(colors.warning, 0.3)}`,
                      fontWeight: 600,
                      height: '28px',
                      '& .MuiChip-deleteIcon': {
                        color: colors.warning,
                        fontSize: '18px',
                        '&:hover': {
                          color: colors.error,
                        }
                      },
                      '& .MuiChip-label': {
                        px: 1.5,
                      }
                    }}
                  />
                )}
                {selectedState && (
                  <Chip
                    label={selectedState}
                    onDelete={() => onStateClick(null)}
                    size="small"
                    sx={{
                      backgroundColor: alpha(colors.primary, 0.15),
                      color: colors.text,
                      borderRadius: '8px',
                      border: `1px solid ${alpha(colors.primary, 0.3)}`,
                      fontWeight: 600,
                      height: '28px',
                      '& .MuiChip-deleteIcon': {
                        color: colors.primary,
                        fontSize: '18px',
                        '&:hover': {
                          color: colors.error,
                        }
                      },
                      '& .MuiChip-label': {
                        px: 1.5,
                      }
                    }}
                  />
                )}
                <Button
                  size="small"
                  onClick={() => {
                    setSelectedDifficulty(null);
                    onStateClick(null);
                  }}
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: colors.textSecondary,
                    textTransform: 'none',
                    minWidth: 'auto',
                    padding: '4px 12px',
                    '&:hover': {
                      color: colors.error,
                      background: alpha(colors.error, 0.1),
                    }
                  }}
                >
                  Clear All
                </Button>
              </Stack>
            </Box>
          </Fade>
          )}
        </Box>
      </HeaderSection>

      {/* Content */}
      {viewMode === 'grid' ? renderGridView() : renderTableView()}

      {/* Menus */}
      <Menu
        anchorEl={sortAnchorEl}
        open={Boolean(sortAnchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            background: colors.cardBg,
            borderRadius: '8px',
            border: `1px solid ${colors.border}`,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
          }
        }}
      >
        <MenuItem
          onClick={() => handleSortOption('name')}
          sx={{
            color: colors.text,
            fontWeight: 500,
            '&:hover': {
              background: alpha(colors.surfaceLight, 0.5),
            }
          }}
        >
          Name {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
        </MenuItem>
        <MenuItem
          onClick={() => handleSortOption('bonus')}
          sx={{
            color: colors.text,
            fontWeight: 500,
            '&:hover': {
              background: alpha(colors.surfaceLight, 0.5),
            }
          }}
        >
          Bonus {sortConfig.key === 'bonus' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
        </MenuItem>
        <MenuItem
          onClick={() => handleSortOption('difficulty')}
          sx={{
            color: colors.text,
            fontWeight: 500,
            '&:hover': {
              background: alpha(colors.surfaceLight, 0.5),
            }
          }}
        >
          Difficulty {sortConfig.key === 'difficulty' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
        </MenuItem>
        <MenuItem
          onClick={() => handleSortOption('successRate')}
          sx={{
            color: colors.text,
            fontWeight: 500,
            '&:hover': {
              background: alpha(colors.surfaceLight, 0.5),
            }
          }}
        >
          Success Rate {sortConfig.key === 'successRate' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
        </MenuItem>
        <MenuItem
          onClick={() => handleSortOption('expiration')}
          sx={{
            color: colors.text,
            fontWeight: 500,
            '&:hover': {
              background: alpha(colors.surfaceLight, 0.5),
            }
          }}
        >
          Expiration {sortConfig.key === 'expiration' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            background: colors.cardBg,
            borderRadius: '8px',
            border: `1px solid ${colors.border}`,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
          }
        }}
      >
        <MenuItem
          onClick={() => handleFilterOption('Easy')}
          sx={{
            color: colors.text,
            fontWeight: 500,
            '&:hover': {
              background: alpha(colors.surfaceLight, 0.5),
            }
          }}
        >
          Easy {selectedDifficulty === 'Easy' && '✓'}
        </MenuItem>
        <MenuItem
          onClick={() => handleFilterOption('Medium')}
          sx={{
            color: colors.text,
            fontWeight: 500,
            '&:hover': {
              background: alpha(colors.surfaceLight, 0.5),
            }
          }}
        >
          Medium {selectedDifficulty === 'Medium' && '✓'}
        </MenuItem>
        <MenuItem
          onClick={() => handleFilterOption('Hard')}
          sx={{
            color: colors.text,
            fontWeight: 500,
            '&:hover': {
              background: alpha(colors.surfaceLight, 0.5),
            }
          }}
        >
          Hard {selectedDifficulty === 'Hard' && '✓'}
        </MenuItem>
      </Menu>
    </MainContainer>
  );
};

export default WebBankTable;
